import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileText, Loader2 } from 'lucide-react';
import type { document } from '@/lib/client';
import { POLL_MS, useDocumentsStore, WORKING } from '@/stores/documentsStore';
import { Zacking } from '@/components/changes/Zacking';
import { DocumentQuestions } from '@/components/changes/DocumentQuestions';

const STATUS_TEXT: Record<document.DocumentStatus, string> = {
  decomposing: 'Reading the document',
  asking: 'Your turn',
  planning: 'Making the plan',
  planned: 'Planned',
  failed: 'Stopped',
};

/** A note of the planner's own working (a draft step it tried and dropped): kept, folded away. Pure. */
const plannerNote = (note: string): boolean => /^step "[^"]+":/.test(note);

/** A routing note as the person reads it: its object first, capitalised. Pure. */
const readableNote = (note: string): string => note.replace(/^(user|call queue|auto receptionist|shared line) "([^"]+)"/i, (_, kind: string, name: string) => `${name} (${kind.toLowerCase()})`).replace(/^./, (c) => c.toUpperCase());

const WORKING_TEXT: Partial<Record<document.DocumentStatus, string>> = {
  decomposing: 'ZAC reads the document, finds what it asks for and what it leaves open. About a minute.',
  planning: 'ZAC matches each change to Zoom\'s API and reads your account for the ids it needs. One to three minutes.',
};

/** A document on its way to a plan (/changes/documents/:id): its rounds of questions, then the plan it became. */
export function DocumentPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const doc = useDocumentsStore((s) => s.byId[id]);
  const load = useDocumentsStore((s) => s.load);
  // A refused answer or plan is kept by document: the reload that follows it would clear the page's own.
  const error = useDocumentsStore((s) => s.errorById[id] ?? s.error);
  const clearError = useDocumentsStore((s) => s.clearError);
  const answer = useDocumentsStore((s) => s.answer);
  const busy = useDocumentsStore((s) => s.busy.answer ?? false);

  useEffect(() => {
    void load(id);
  }, [id, load]);

  // A job runs: read the document again until it ends.
  const working = doc !== undefined && WORKING.has(doc.status);
  useEffect(() => {
    if (!working) return;
    const timer = setInterval(() => void load(id), POLL_MS);
    return () => clearInterval(timer);
  }, [working, id, load]);

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-4 overflow-y-auto p-4 sm:p-6">
      <Link to="/changes" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Changes</Link>
      {doc === undefined ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-foreground">{doc.filename}</h1>
              <p className="text-xs text-muted-foreground">{STATUS_TEXT[doc.status]} · round {doc.round} of {doc.max_rounds} · ${doc.cost_usd.toFixed(2)} of model calls</p>
            </div>
          </div>
          {error && (
            <div className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <span>{error}</span>
              <button onClick={() => clearError(id)} className="text-xs underline">dismiss</button>
            </div>
          )}
          {working && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{WORKING_TEXT[doc.status]}</p>
              <Zacking doc={doc} />
            </div>
          )}
          {doc.status === 'failed' && doc.error && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{doc.error}</p>}
          {doc.status === 'failed' && doc.round === 0 && (
            <button onClick={() => void answer(doc.id, [])} disabled={busy} className="w-fit rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-secondary disabled:opacity-50">
              Read it again
            </button>
          )}
          {doc.status === 'planned' && doc.plan_id && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <p className="text-sm text-foreground">The plan is ready to review. Nothing changes on your account until you approve it.</p>
              {doc.notes.filter((note) => !plannerNote(note)).length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-sm text-foreground">{doc.notes.filter((note) => !plannerNote(note)).map((note) => <li key={note}>{readableNote(note)}</li>)}</ul>
              )}
              {doc.notes.some(plannerNote) && (
                <details className="mt-2 text-xs text-muted-foreground">
                  <summary className="cursor-pointer select-none hover:text-foreground">How ZAC worked it out ({doc.notes.filter(plannerNote).length} drafts it tried and dropped)</summary>
                  <ul className="mt-1 list-disc pl-5">{doc.notes.filter(plannerNote).map((note) => <li key={note}>{note}</li>)}</ul>
                </details>
              )}
              <button onClick={() => navigate(`/changes/${doc.plan_id}`)} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                Open the plan <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {doc.objects.length > 0 && (
            <section className="rounded-xl border border-border bg-card p-4">
              <h2 className="text-sm font-medium text-foreground">What the document asks for</h2>
              <ul className="mt-2 space-y-1 text-sm">
                {doc.objects.map((o) => (
                  <li key={o.key} className="flex gap-2">
                    <span className="w-16 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{o.action}</span>
                    <span className="text-foreground">{o.kind} <span className="font-medium">"{o.name}"</span></span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {(doc.status === 'asking' || (doc.status === 'failed' && doc.round > 0)) && <DocumentQuestions key={doc.round} doc={doc} />}
        </>
      )}
    </div>
  );
}
